import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckIcon, FileTextIcon, SendIcon, SaveIcon, CalendarClockIcon, BookmarkPlusIcon, StarIcon, AlertTriangleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { AudienceSelector } from './notice/AudienceSelector';
import { ChannelEditor } from './notice/ChannelEditor';
import { NoticePreview, type PreviewTab } from './notice/NoticePreview';
import { SendScheduler } from './notice/SendScheduler';
import { VariablesBlock } from './notice/VariablesBlock';
import { AtnaBrief } from './notice/AtnaBrief';
import { AiReviewPanel } from './notice/AiReviewPanel';
import type { AtnaDraft } from '../utils/atna';
import { hasContent, stripHtml } from './RichTextEditor';
import { auditNotice, type AiContext, type AuditFinding } from '../utils/aiAssist';
import { getIncompleteVariables } from '../utils/variables';
import { SYSTEM_VARIABLES, type NoticeVariable } from '../types/variables';
import { BUILT_IN_TEMPLATES, CATEGORIES, SECTORS, getCategoryLabel, type NoticeTemplate } from '../data/communications';
import { EMPTY_AUDIENCE, EMPTY_CHANNELS, EMPTY_SCHEDULE, type AudienceState, type ChannelsState, type ScheduleState } from '../types/composer';
import { getAudienceTotal } from '../utils/audience';
import type { Notice } from '../types/notice';

/** Converts template plain text into the HTML the rich editors expect. */
function textToHtml(text: string, imageUrl?: string): string {
  const paragraphs = text.split('\n\n').map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`).join('');
  const image = imageUrl ? `<img src="${imageUrl}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />` : '';
  return `${image}${paragraphs}`;
}
interface NoticeComposerProps {
  legacyMode: boolean;
  onToggleLegacy: (value: boolean) => void;
  /** Language the notice content is being written in (FR/EN content tabs). */
  contentLang: 'fr' | 'en';
  /** Notice being edited, or null when composing a new one. */
  editingNotice?: Notice | null;
  customTemplates: NoticeTemplate[];
  onSaveTemplate: (template: NoticeTemplate, category: string) => void;
  onSubmit: (mode: 'draft' | 'send') => void;
}
export function NoticeComposer({
  legacyMode,
  onToggleLegacy,
  contentLang,
  editingNotice = null,
  customTemplates,
  onSaveTemplate,
  onSubmit
}: NoticeComposerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [category, setCategory] = useState(editingNotice?.category ?? '');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [title, setTitle] = useState(editingNotice?.title ?? '');
  const [description, setDescription] = useState('');
  const [channels, setChannels] = useState<ChannelsState>(EMPTY_CHANNELS);
  const [audience, setAudience] = useState<AudienceState>(EMPTY_AUDIENCE);
  const [schedule, setSchedule] = useState<ScheduleState>(EMPTY_SCHEDULE);
  /** Variables live only for this notice — never saved to the configuration. */
  const [variables, setVariables] = useState<NoticeVariable[]>(SYSTEM_VARIABLES);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [previewTab, setPreviewTab] = useState<PreviewTab>('push');
  const [confirmation, setConfirmation] = useState<string | null>(null);

  // Seed the form from the notice being edited.
  useEffect(() => {
    if (!editingNotice) return;
    setTitle(editingNotice.title);
    setCategory(editingNotice.category);
    setChannels(() => {
      const next = {
        ...EMPTY_CHANNELS
      };
      (Object.keys(next) as (keyof ChannelsState)[]).forEach(key => {
        next[key] = {
          ...next[key],
          active: editingNotice.channels.includes(key as never)
        } as never;
      });
      return next;
    });
    setAudience(() => ({
      ...EMPTY_AUDIENCE,
      zone: editingNotice.audience.kind,
      selectedSectors: editingNotice.audience.kind === 'sector' ? SECTORS.filter(s => editingNotice.audience.detail?.fr.includes(s.label.fr.replace('Secteurs ', ''))).map(s => s.id) : []
    }));
    setSchedule({
      ...EMPTY_SCHEDULE,
      mode: editingNotice.status === 'scheduled' ? 'scheduled' : 'now',
      date: editingNotice.status === 'scheduled' ? editingNotice.date.slice(0, 10) : '',
      time: editingNotice.status === 'scheduled' ? editingNotice.date.slice(11, 16) : '09:00'
    });
    const firstChannel = editingNotice.channels[0];
    if (firstChannel && firstChannel !== 'social') {
      setPreviewTab(firstChannel as PreviewTab);
    }
  }, [editingNotice]);
  const audienceTotal = useMemo(() => getAudienceTotal(audience), [audience]);
  const audienceLabel = audience.zone === 'org' ? t("Toute l'organisation", 'Entire organization') : audience.zone === 'sector' ? `${audience.selectedSectors.length} ${t('secteur(s)', 'district(s)')}` : t('Autour d’un lieu', 'Around a location');

  /** Everything the assistant reasons about, rebuilt as the notice changes. */
  const aiContext: AiContext = useMemo(() => ({
    title,
    category,
    categoryLabel: category ? getCategoryLabel(category)[lang] : '',
    audienceTotal,
    audienceLabel,
    channels,
    variables,
    contentLang
  }), [title, category, lang, audienceTotal, audienceLabel, channels, variables, contentLang]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [findings, setFindings] = useState<AuditFinding[]>([]);

  /** Fills the whole form from what ATNA wrote, then jumps to its main channel. */
  const applyAtnaDraft = (draft: AtnaDraft) => {
    setTitle(draft.title);
    setCategory(draft.category);
    setSelectedTemplate(null);
    setChannels(draft.channels);
    setAudience(prev => ({
      ...prev,
      ...draft.audience
    }));
    const firstChannel = (Object.keys(draft.channels) as (keyof typeof draft.channels)[]).find(key => draft.channels[key].active && key !== 'social');
    if (firstChannel) setPreviewTab(firstChannel as PreviewTab);
  };
  const runReview = () => {
    setReviewOpen(true);
    setReviewLoading(true);
    window.setTimeout(() => {
      setFindings(auditNotice(aiContext));
      setReviewLoading(false);
    }, 900);
  };
  const availableTemplates = useMemo(() => {
    if (!category) return [];
    return [...customTemplates.filter(tpl => tpl.id.startsWith(`${category}:`)), ...(BUILT_IN_TEMPLATES[category] ?? [])];
  }, [category, customTemplates]);
  const patchAudience = (patch: Partial<AudienceState>) => setAudience(prev => ({
    ...prev,
    ...patch
  }));
  const patchSchedule = (patch: Partial<ScheduleState>) => setSchedule(prev => ({
    ...prev,
    ...patch
  }));
  const toggleChannel = (key: keyof ChannelsState) => {
    const willActivate = !channels[key].active;
    setChannels(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        active: willActivate
      }
    }));
    if (willActivate) setPreviewTab(key as PreviewTab);
  };
  const updateChannel = <K extends keyof ChannelsState,>(key: K, patch: Partial<ChannelsState[K]>) => setChannels(prev => ({
    ...prev,
    [key]: {
      ...prev[key],
      ...patch
    }
  }));
  const applyTemplate = (tpl: NoticeTemplate) => {
    setSelectedTemplate(tpl.id);
    setTitle(tpl.title);
    setDescription(tpl.push.message);
    setChannels(prev => ({
      ...prev,
      email: {
        active: true,
        subject: tpl.email.subject,
        body: textToHtml(tpl.email.body, tpl.imageUrl),
        attachments: []
      },
      sms: {
        active: true,
        text: tpl.sms
      },
      push: {
        active: true,
        title: tpl.push.title,
        message: tpl.push.message
      },
      social: tpl.social ? {
        active: true,
        postText: textToHtml(tpl.social, tpl.imageUrl)
      } : prev.social
    }));
    setPreviewTab('push');
  };
  const hasAnyContent = Boolean(title.trim()) || hasContent(channels.email.body) || Boolean(channels.sms.text) || hasContent(channels.social.postText);

  /** Variables used in the content but left without a value for this language. */
  const incompleteVariables = useMemo(() => getIncompleteVariables(variables, channels, contentLang), [variables, channels, contentLang]);
  const canSubmit = incompleteVariables.length === 0 && (schedule.mode === 'template' ? Boolean(category) && Boolean(schedule.templateName.trim()) && hasAnyContent : schedule.mode === 'scheduled' ? Boolean(schedule.date) && hasAnyContent : hasAnyContent);
  const submitLabel = schedule.mode === 'template' ? t('Enregistrer le modèle', 'Save template') : schedule.mode === 'scheduled' ? t("Programmer l'envoi", 'Schedule sending') : editingNotice ? t('Enregistrer et envoyer', 'Save and send') : t('Envoyer maintenant', 'Send now');
  const SubmitIcon = schedule.mode === 'template' ? BookmarkPlusIcon : schedule.mode === 'scheduled' ? CalendarClockIcon : SendIcon;
  const handleSubmit = () => {
    if (!canSubmit) return;
    if (schedule.mode === 'template') {
      const name = schedule.templateName.trim();
      const tpl: NoticeTemplate = {
        id: `${category}:${Date.now()}`,
        name: {
          fr: name,
          en: name
        },
        description: {
          fr: schedule.templateDescription.trim() || 'Modèle enregistré par votre équipe',
          en: schedule.templateDescription.trim() || 'Template saved by your team'
        },
        title,
        email: {
          subject: channels.email.subject,
          body: stripHtml(channels.email.body)
        },
        sms: channels.sms.text,
        push: {
          title: channels.push.title,
          message: channels.push.message
        },
        social: stripHtml(channels.social.postText) || undefined,
        custom: true
      };
      onSaveTemplate(tpl, category);
      setConfirmation(t(`Modèle « ${name} » enregistré`, `Template “${name}” saved`));
      patchSchedule({
        templateName: '',
        templateDescription: ''
      });
      window.setTimeout(() => setConfirmation(null), 4000);
      return;
    }
    onSubmit('send');
  };
  return <div className="font-['Inter'] bg-[#F7F7F8] pb-28" data-id="element-4052" data-mp="5k8c74310o4f">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start" data-id="element-4053" data-mp="5ixb5c0ytuh0">
        {/* LEFT — FORM */}
        <div className="space-y-10 min-w-0" data-id="element-4054" data-mp="fbcq00gyu2xo">
          {/* 1. ATNA BRIEF */}
          <AtnaBrief channels={channels} contentLang={contentLang} onGenerate={applyAtnaDraft} data-id="element-4055" data-mp="k9wdw6f4doso" />

          {/* 2. GENERAL INFORMATION */}
          <section className="space-y-6" data-id="element-4056" data-mp="o9f0r7qi49y5">
            <div className="flex items-center justify-between gap-4" data-id="element-4057" data-mp="28tsgy4bkgte2">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-4058" data-mp="24ryhu0h0rxg0">
                {t('Informations générales', 'General information')}
              </h2>
              <label className="flex items-center gap-2 cursor-pointer select-none" data-id="element-4059" data-mp="1ztnca1xrc93l">
                <span className="text-xs font-medium text-slate-400" data-id="element-4060" data-mp="1uusriug8cagu">
                  {t('Version classique', 'Classic version')}
                </span>
                <button type="button" role="switch" aria-checked={legacyMode} aria-label={t('Version classique', 'Classic version')} onClick={() => onToggleLegacy(!legacyMode)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${legacyMode ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-4061" data-mp="2nig7txagwexo">
                  <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${legacyMode ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-4062" data-mp="18lvevufzf31f" />
                </button>
              </label>
            </div>

            <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-6" data-id="element-4063" data-mp="pvdr84a0axmx">
              <div data-id="element-4064" data-mp="bvmhifqiw3eq">
                <label htmlFor="notice-title" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4065" data-mp="bw4ni42zlvzu">
                  {t('Titre de l’avis', 'Notice title')}
                </label>
                <input id="notice-title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('Ex. : Avis d’ébullition d’eau préventif', 'E.g. Preventive boil water advisory')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4066" data-mp="26717mxng44v0" />
              </div>

              {/* Category grid */}
              <div data-id="element-4067" data-mp="2z3siyzt92hmw">
                <span className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4068" data-mp="2z4bv1zw7ybt0">
                  {t('Catégorie', 'Category')}
                </span>
                <div role="radiogroup" aria-label={t('Catégorie', 'Category')} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-id="element-4069" data-mp="3d0z24vhvyicn">
                  {CATEGORIES.map(c => {
                  const Icon = c.icon;
                  const active = category === c.value;
                  return <button key={c.value} type="button" role="radio" aria-checked={active} onClick={() => {
                    setCategory(c.value);
                    setSelectedTemplate(null);
                  }} className={`flex flex-col items-center justify-center gap-2 px-2 py-3.5 rounded-xl border text-center transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/50 ring-1 ring-[#4F46E5]' : 'border-slate-200 bg-white hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4070" data-mp="1g6yqix5e2ixb">
                        <span className={`inline-flex w-9 h-9 rounded-lg items-center justify-center ${active ? 'bg-[#4F46E5] text-white' : c.urgent ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-500'}`} data-id="element-4071" data-mp="dpm2qw72hq0k">
                          <Icon className="w-4 h-4" data-id="element-4072" data-mp="3tbkksuc49sa2" />
                        </span>
                        <span className={`text-xs font-medium leading-tight ${active ? 'text-[#4F46E5]' : 'text-slate-600'}`} data-id="element-4073" data-mp="3tdn7b4e293a1">
                          {c.label[lang]}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* Template suggestions */}
              <AnimatePresence data-id="element-4074" data-mp="35kyeb7hrgg26">
                {category && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-4075" data-mp="30n59ahs71s3h">
                    <div className="pt-1" data-id="element-4076" data-mp="2h2eorpgyh5iz">
                      <div className="flex items-center gap-2 mb-3" data-id="element-4077" data-mp="2m06hglpfcvo8">
                        <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-4078" data-mp="2m0ga6h1utgxm" />
                        <span className="block text-sm font-medium text-slate-700" data-id="element-4079" data-mp="39qtutmi40di6">
                          {t('Modèles suggérés', 'Suggested templates')}
                        </span>
                      </div>
                      {availableTemplates.length > 0 ? <div className="space-y-2" data-id="element-4080" data-mp="34yir08k907kc">
                          {availableTemplates.map(tpl => {
                      const active = selectedTemplate === tpl.id;
                      return <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)} className={`w-full flex items-center gap-3 text-left p-3 rounded-lg border transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/40' : 'border-slate-200 hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4081" data-mp="1aqvq552bx5g">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-4082" data-mp="y3qj6uo2eizv">
                                  {active ? <CheckIcon className="w-4 h-4" data-id="element-4083" data-mp="32kd8oiesi8tv" /> : tpl.custom ? <StarIcon className="w-4 h-4" data-id="element-4084" data-mp="37iom134an1gu" /> : <FileTextIcon className="w-4 h-4" data-id="element-4085" data-mp="3bk0erhigs2t9" />}
                                </div>
                                <div className="min-w-0 flex-1" data-id="element-4086" data-mp="3gdw9mb3fl9k7">
                                  <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2" data-id="element-4087" data-mp="3la33h5ijyuls">
                                    {tpl.name[lang]}
                                    {tpl.custom && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-[#4F46E5] text-[10px] font-bold uppercase tracking-wide" data-id="element-4088" data-mp="3v6y1lnawyszj">
                                        {t('Modèle', 'Template')}
                                      </span>}
                                  </p>
                                  <p className="text-xs text-slate-500 truncate" data-id="element-4089" data-mp="1xdn93pkbd22p">
                                    {tpl.description[lang]}
                                  </p>
                                </div>
                              </button>;
                    })}
                          <p className="text-xs text-slate-400 pt-1" data-id="element-4090" data-mp="1jmi5kcdtrpn3">
                            {t('Cliquez sur un modèle pour remplir automatiquement le contenu des canaux.', 'Click a template to automatically fill in the channel content.')}
                          </p>
                        </div> : <p className="text-sm text-slate-400 py-2" data-id="element-4091" data-mp="22bf26mwp2u8h">
                          {t('Aucun modèle disponible pour cette catégorie. Rédigez votre contenu dans les canaux ci-dessous.', 'No template available for this category. Write your content in the channels below.')}
                        </p>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </section>

          {/* 3. AUDIENCE */}
          <AudienceSelector audience={audience} onChange={patchAudience} audienceTotal={audienceTotal} showSuggestions={showSuggestions} onShowSuggestions={setShowSuggestions} data-id="element-4092" data-mp="39nlu15riuc50" />

          {/* 4. VARIABLES */}
          <VariablesBlock variables={variables} onChange={setVariables} channels={channels} contentLang={contentLang} data-id="element-4093" data-mp="167o1fpqg7wy" />

          {/* 5. CHANNELS */}
          <ChannelEditor channels={channels} onToggle={toggleChannel} onUpdate={updateChannel} audienceTotal={audienceTotal} variables={variables} contentLang={contentLang} aiContext={aiContext} data-id="element-4094" data-mp="2lx85vqc24xfp" />

          {/* 6. SENDING TIME */}
          <SendScheduler schedule={schedule} onChange={patchSchedule} categoryLabel={category ? getCategoryLabel(category)[lang] : null} data-id="element-4095" data-mp="2btjbvg2dhzoz" />
        </div>

        {/* RIGHT — LIVE PREVIEW */}
        <div className="lg:sticky lg:top-8" data-id="element-4096" data-mp="2pkg61bby6oy5">
          <NoticePreview channels={channels} title={title} description={description} categoryLabel={category ? getCategoryLabel(category)[lang] : t('Général', 'General')} previewTab={previewTab} onPreviewTabChange={setPreviewTab} variables={variables} contentLang={contentLang} data-id="element-4097" data-mp="2pjmu7vy4t995" />
        </div>
      </div>

      {/* ACTION BAR */}
      <div className="sticky bottom-0 mt-10 -mb-28 bg-white/95 backdrop-blur border-t border-slate-200 z-30" data-id="element-4098" data-mp="1a3e9byshd2l5">
        <div className="max-w-6xl mx-auto px-1 py-3 flex flex-wrap items-center gap-3 justify-between" data-id="element-4099" data-mp="122gem3zihhh8">
          <div className="min-w-0" role="status" aria-live="polite" data-id="element-4100" data-mp="x4onvdals1em">
            {incompleteVariables.length > 0 && <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg" data-id="element-4101" data-mp="1c1xh6cjgvf8z">
                <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-4102" data-mp="1l1kn9bn5598d" />
                {incompleteVariables.length}{' '}
                {t('variable(s) sans valeur : ', 'variable(s) without a value: ')}
                <span className="font-semibold" data-id="element-4103" data-mp="1us6uoikpetfz">
                  {incompleteVariables.map(v => v.label[lang]).join(', ')}
                </span>
              </span>}
            {confirmation && incompleteVariables.length === 0 && <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg" data-id="element-4104" data-mp="23slippp1ymn6">
                <CheckIcon className="w-4 h-4" data-id="element-4105" data-mp="2iesjmo9669ih" />
                {confirmation}
              </span>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-4106" data-mp="2ndlsg3dihccb">
            <button type="button" onClick={runReview} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-4107" data-mp="29f5kk93wxfsl">
              <SparklesIcon className="w-4 h-4" data-id="element-4108" data-mp="24ezq6l69df6t" />
              {t("Vérifier l'avis", 'Review the notice')}
            </button>

            <button type="button" onClick={() => onSubmit('draft')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4109" data-mp="1qow7232oe1ll">
              <SaveIcon className="w-4 h-4" data-id="element-4110" data-mp="1lnqec590mec2" />
              {t('Enregistrer le brouillon', 'Save draft')}
            </button>

            <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#4F46E5]" data-id="element-4111" data-mp="2o1egyg20gru7">
              <SubmitIcon className="w-4 h-4" data-id="element-4112" data-mp="25chpsl1wufg7" />
              {submitLabel}
            </button>
          </div>
        </div>
      </div>

      <AiReviewPanel open={reviewOpen} loading={reviewLoading} findings={findings} onClose={() => setReviewOpen(false)} data-id="element-4113" data-mp="20ey8fc9x1mk1" />
    </div>;
}